/**
 * CLVisit-based airport detection.
 *
 * When a visit event fires from the native plugin, we match the coordinates
 * against our airport database. If within PROXIMITY_KM of a known airport,
 * we update the user's current_airport in Supabase.
 *
 * Feature flag: set VISIT_MONITOR_ENABLED = false to disable entirely.
 */

import { registerPlugin, Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabase';
import { allAirportCoords } from '../utils/airportCoords';

// ── Feature flag ──
const VISIT_MONITOR_ENABLED = false; // Deferred to v1.1 — avoids "Always" location permission friction

// Max distance (km) from airport center to count as "at the airport"
const PROXIMITY_KM = 5;

// ── Native plugin interface ──

interface VisitEvent {
  latitude: number;
  longitude: number;
  horizontalAccuracy: number;
  arrivalDate?: string;
  departureDate?: string;
  isArrival?: boolean;
}

interface VisitMonitorNative {
  startMonitoring(): Promise<{ started: boolean }>;
  stopMonitoring(): Promise<{ stopped: boolean }>;
  getPermissionStatus(): Promise<{ status: string }>;
  requestPermission(): Promise<{ status: string }>;
  addListener(event: 'visit', cb: (data: VisitEvent) => void): Promise<{ remove: () => void }>;
  addListener(event: 'permissionChange', cb: (data: { status: string }) => void): Promise<{ remove: () => void }>;
}

let plugin: VisitMonitorNative | null = null;

function getPlugin(): VisitMonitorNative | null {
  if (!Capacitor.isNativePlatform()) return null;
  if (!plugin) {
    // registerPlugin returns a proxy that calls the native side
    plugin = registerPlugin<VisitMonitorNative>('VisitMonitor');
  }
  return plugin;
}

// ── Haversine distance in km ──

function distanceKm(
  lat1: number, lon1: number,
  lat2: number, lon2: number,
): number {
  const R = 6371; // Earth radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

// ── Airport matching ──

function findNearestAirport(
  lat: number, lon: number,
): { iata: string; distKm: number } | null {
  const airports = allAirportCoords();
  let best: { iata: string; distKm: number } | null = null;

  for (const ap of airports) {
    const d = distanceKm(lat, lon, ap.lat, ap.lon);
    if (d <= PROXIMITY_KM && (!best || d < best.distKm)) {
      best = { iata: ap.iata, distKm: d };
    }
  }

  return best;
}

// ── Supabase update ──

async function updateCurrentAirport(
  userId: string,
  airport: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({
      current_airport: airport,
      last_seen_at: new Date().toISOString(),
    } as never)
    .eq('id' as never, userId);

  if (error) {
    console.warn('[VisitMonitor] Failed to update current_airport:', error.message);
  }
}

// ── Public API ──

let removeListener: (() => void) | null = null;

/**
 * Initialize visit monitoring. Call once on app startup.
 * Returns a cleanup function.
 */
export async function initVisitMonitor(userId: string): Promise<() => void> {
  if (!VISIT_MONITOR_ENABLED) {
    console.log('[VisitMonitor] Disabled by feature flag');
    return () => {};
  }

  const p = getPlugin();
  if (!p) {
    console.log('[VisitMonitor] Not on native platform, skipping');
    return () => {};
  }

  // Check permission status
  const { status } = await p.getPermissionStatus();
  if (status !== 'authorizedAlways') {
    console.log(`[VisitMonitor] Permission is '${status}', not starting`);
    return () => {};
  }

  // Listen for visit events
  const handle = await p.addListener('visit', (event) => {
    console.log('[VisitMonitor] Visit event:', event);

    if (event.isArrival || !event.departureDate) {
      // User arrived somewhere — check if it's an airport
      const match = findNearestAirport(event.latitude, event.longitude);
      if (match) {
        console.log(`[VisitMonitor] Arrived at ${match.iata} (${match.distKm.toFixed(1)}km)`);
        updateCurrentAirport(userId, match.iata);
      }
    } else if (event.departureDate) {
      // User departed — clear current airport
      const match = findNearestAirport(event.latitude, event.longitude);
      if (match) {
        console.log(`[VisitMonitor] Departed from ${match.iata}`);
        updateCurrentAirport(userId, null);
      }
    }
  });

  removeListener = handle.remove;

  // Start monitoring
  try {
    await p.startMonitoring();
    console.log('[VisitMonitor] Monitoring started');
  } catch (err) {
    console.warn('[VisitMonitor] Failed to start:', err);
  }

  return () => {
    removeListener?.();
    removeListener = null;
    p.stopMonitoring().catch(() => {});
  };
}

/**
 * Request location permission (two-step: WhenInUse → Always).
 * Returns the final permission status.
 */
export async function requestVisitPermission(): Promise<string> {
  const p = getPlugin();
  if (!p) return 'unavailable';

  const { status } = await p.requestPermission();
  return status;
}

/**
 * Get current permission status without prompting.
 */
export async function getVisitPermissionStatus(): Promise<string> {
  const p = getPlugin();
  if (!p) return 'unavailable';

  const { status } = await p.getPermissionStatus();
  return status;
}
